import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["nowPlaying", "queue", "playlists", "searchInput", "searchResults", "djProfile", "fullscreenBtn", "voteScore", "compactPlayer", "expandedPlayer", "mixtapeFormFields", "mixtapeName", "mixtapeUri"]
  static values = { pollUrl: String, queueUrl: String }

  connect() {
    this.expanded = localStorage.getItem("boombox_expanded") === "true"
    this.pollInterval = setInterval(() => this.refreshNowPlaying(), 5000)
    this.refreshNowPlaying()
    this.refreshQueue()
    this.refreshPlaylists()
  }

  disconnect() {
    if (this.pollInterval) clearInterval(this.pollInterval)
  }

  async refreshNowPlaying() {
    try {
      const response = await fetch(this.pollUrlValue, { headers: this.headers })
      if (response.ok) {
        this.nowPlayingTarget.innerHTML = await response.text()
        this.applyExpandedState()
      }
    } catch (e) {}
  }

  applyExpandedState() {
    const container = this.nowPlayingTarget
    if (this.expanded) {
      container.classList.remove("shrink-0")
      container.classList.add("flex-1", "min-h-0")
      if (container.nextElementSibling) container.nextElementSibling.classList.add("hidden")
      if (this.hasCompactPlayerTarget) this.compactPlayerTarget.classList.add("hidden")
      if (this.hasExpandedPlayerTarget) this.expandedPlayerTarget.classList.remove("hidden")
    } else {
      container.classList.add("shrink-0")
      container.classList.remove("flex-1", "min-h-0")
      if (container.nextElementSibling) container.nextElementSibling.classList.remove("hidden")
      if (this.hasCompactPlayerTarget) this.compactPlayerTarget.classList.remove("hidden")
      if (this.hasExpandedPlayerTarget) this.expandedPlayerTarget.classList.add("hidden")
    }
  }

  async refreshQueue() {
    try {
      const response = await fetch(this.queueUrlValue, { headers: this.headers })
      if (response.ok) {
        this.queueTarget.innerHTML = await response.text()
      }
    } catch (e) {}
  }

  async refreshPlaylists() {
    try {
      if (!this.hasPlaylistsTarget) return
      const response = await fetch("/boombox/playlists", { headers: this.headers })
      if (response.ok) {
        this.playlistsTarget.innerHTML = await response.text()
      }
    } catch (e) {}
  }

  async play() {
    await this.postAction("/boombox/play")
  }

  async pause() {
    await this.postAction("/boombox/pause")
  }

  async next() {
    await this.postAction("/boombox/next")
    setTimeout(() => this.refreshNowPlaying(), 500)
  }

  async previous() {
    await this.postAction("/boombox/previous")
    setTimeout(() => this.refreshNowPlaying(), 500)
  }

  async setVolume(event) {
    await this.postAction("/boombox/volume", { percent: event.target.value })
  }

  async queueTrack(event) {
    const btn = event.currentTarget
    const data = {
      uri: btn.dataset.uri,
      track_name: btn.dataset.trackName || btn.closest("[data-track-name]")?.dataset.trackName,
      artist_name: btn.dataset.artistName || btn.closest("[data-artist-name]")?.dataset.artistName
    }
    await this.postAction("/boombox/queue", data)
    this.refreshQueue()
  }

  async playPlaylist(event) {
    const btn = event.currentTarget
    const uri = btn.dataset.uri
    const name = btn.dataset.playlistName || btn.querySelector("span")?.textContent?.trim()
    await this.postAction("/boombox/play", { context_uri: uri, playlist_name: name })
    setTimeout(() => {
      this.refreshNowPlaying()
      this.refreshQueue()
    }, 500)
  }

  async search() {
    const query = this.searchInputTarget.value.trim()
    if (!query) return

    try {
      const response = await fetch(`/boombox/search?q=${encodeURIComponent(query)}`, {
        headers: this.headers
      })
      if (response.ok) {
        this.searchResultsTarget.innerHTML = await response.text()
      }
    } catch (e) {}
  }

  // Favourites
  async toggleFavourite(event) {
    const btn = event.currentTarget
    const data = {
      spotify_track_uri: btn.dataset.uri,
      track_name: btn.dataset.trackName,
      artist_name: btn.dataset.artistName,
      album_art_url: btn.dataset.albumArtUrl
    }

    try {
      const response = await fetch("/boombox/favourites", {
        method: "POST",
        headers: { ...this.headers, "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(data)
      })
      if (response.ok) {
        btn.classList.add("text-fuchsia-400")
        btn.classList.remove("text-fuchsia-400/40")
      }
    } catch (e) {}
  }

  // Playlist follow/unfollow -- every playlist in this panel comes from
  // GET /me/playlists, which by definition only returns playlists the
  // current user already follows. So the only meaningful action here is
  // unfollow: it removes the playlist from the Spotify library and, to
  // match, removes it from this panel immediately.
  async toggleFollowPlaylist(event) {
    const btn = event.currentTarget
    const playlistId = btn.dataset.playlistId
    const container = document.getElementById(btn.dataset.domId)

    try {
      const response = await fetch(`/boombox/playlists/${playlistId}/follow`, {
        method: "DELETE",
        headers: this.headers
      })
      if (response.ok && container) {
        container.remove()
      }
    } catch (e) {}
  }

  // Voting
  async upvote(event) {
    await this.castVote(event.currentTarget.dataset.uri, 1)
  }

  async downvote(event) {
    await this.castVote(event.currentTarget.dataset.uri, -1)
  }

  async castVote(uri, value) {
    try {
      const response = await fetch("/boombox/vote", {
        method: "POST",
        headers: { ...this.headers, "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ spotify_track_uri: uri, value })
      })
      if (response.ok && this.hasVoteScoreTarget) {
        const data = await response.json()
        this.voteScoreTarget.textContent = data.score
      }
    } catch (e) {}
  }

  // Expand/compact now playing
  toggleNowPlaying() {
    this.expanded = !this.expanded
    localStorage.setItem("boombox_expanded", this.expanded)
    this.applyExpandedState()
  }

  // Mixtapes
  showMixtapeForm() {
    if (this.hasMixtapeFormFieldsTarget) this.mixtapeFormFieldsTarget.classList.remove("hidden")
  }

  hideMixtapeForm() {
    if (this.hasMixtapeFormFieldsTarget) this.mixtapeFormFieldsTarget.classList.add("hidden")
    if (this.hasMixtapeNameTarget) this.mixtapeNameTarget.value = ""
    if (this.hasMixtapeUriTarget) this.mixtapeUriTarget.value = ""
  }

  async saveMixtape() {
    const name = this.mixtapeNameTarget.value.trim()
    let uri = this.mixtapeUriTarget.value.trim()
    if (!name || !uri) return

    // Convert Spotify URL to URI if needed
    const urlMatch = uri.match(/playlist\/([a-zA-Z0-9]+)/)
    if (urlMatch) uri = `spotify:playlist:${urlMatch[1]}`

    try {
      const response = await fetch("/boombox/mixtapes", {
        method: "POST",
        headers: { ...this.headers, "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ name, spotify_uri: uri })
      })
      if (response.ok) {
        window.location.reload()
      }
    } catch (e) {}
  }

  // Fullscreen
  toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      this.element.requestFullscreen()
    }
  }

  // DJ profiles
  closeDj() {
    if (this.hasDjProfileTarget) {
      this.djProfileTarget.innerHTML = ""
      this.djProfileTarget.classList.add("hidden")
    }
  }

  handleDjClick(event) {
    if (event.target.closest("[data-close-dj]")) {
      this.closeDj()
    }
  }

  async loadDj(event) {
    const djId = event.currentTarget.dataset.djId
    try {
      const response = await fetch(`/boombox/dj/${djId}`, { headers: this.headers })
      if (response.ok && this.hasDjProfileTarget) {
        this.djProfileTarget.innerHTML = await response.text()
        this.djProfileTarget.classList.remove("hidden")
      }
    } catch (e) {}
  }

  async postAction(url, body = {}) {
    try {
      await fetch(url, {
        method: "POST",
        headers: { ...this.headers, "Content-Type": "application/json" },
        body: JSON.stringify(body)
      })
    } catch (e) {}
  }

  get headers() {
    return {
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
      "Accept": "text/html"
    }
  }
}
